import type { PlannerProject, WardrobeModule } from "../types";
import { createId, maxHeight } from "./project";
import { shelvingCellId } from "./shelving";
import { usableRoomHeightMm } from "./moduleRules";
import { installationSpanMm } from "./installation";
import { BACK_WALL_LENGTH_MM, isStackedModule, materializeWallOffsets, resolveModuleWallOffset, moduleOverlapsFloor } from "./layout";

/**
 * Чистый комод: из категории «Комоды», один ряд, все ячейки — ящики. Такой
 * открывается в разделе «Комоды»; низкие модели с дверцами — как стеллаж.
 */
export function isChestShelf(module: WardrobeModule | undefined, category?: string) {
  if (!module || module.type !== "shelving") return false;
  if (module.shelvingKind === "chest") return true;
  if (category !== "chest" || (module.shelvingRows ?? 1) !== 1) return false;
  const columns = module.shelvingColumns ?? 1;
  const cells = module.shelvingCells ?? [];
  return Array.from({ length: columns }, (_, column) => cells.find(cell => cell.column === column && cell.row === 0))
    .every(cell => cell?.content === "drawers");
}

/** A template is always a new independent item. Existing placements and finishes stay intact. */
export function appendCatalogShelf(project: PlannerProject, source: WardrobeModule): PlannerProject {
  if (source.type !== "shelving") throw new Error("Эта модель не является стеллажом");
  if (source.height > usableRoomHeightMm(project)) throw new Error("Модель не помещается у потолка: она входит в потолочную падугу. Сначала увеличьте высоту комнаты");
  const draft = structuredClone(project);
  const offsets = materializeWallOffsets(draft);
  draft.modules.forEach(m => { m.wallOffset = offsets.get(m.id); });
  const module = structuredClone(source);
  module.id = createId("module");
  module.shelvingCells = module.shelvingCells?.map(cell => ({...cell, id: shelvingCellId(module.id, cell.column, cell.row)}));
  module.items = module.items.map(item => ({...item, id: createId("item")}));
  delete module.stackedOnId; delete module.endAttachedToId;
  const floor = draft.modules.filter(m => !isStackedModule(m));
  // Одна модель в пустой комнате — по центру задней стены, а не в углу (приёмка 05.10, cat-06).
  module.wallOffset = floor.length
    ? Math.max(0, ...floor.map(m => (m.wallOffset ?? 0) + installationSpanMm(m) + 20))
    : Math.max(0, Math.round((BACK_WALL_LENGTH_MM - installationSpanMm(module)) / 2));
  draft.modules.push(module);
  module.wallOffset = resolveModuleWallOffset(draft, module.id, module.wallOffset, 1);
  if (moduleOverlapsFloor(draft, module.id)) throw new Error("В комнате нет свободного места для этой модели. Освободите место или выберите более узкую основу");
  draft.selectedModuleId = module.id; draft.selectedItemId = null; draft.selectedShelvingCellId = null;
  return draft;
}

/**
 * Готовая основа в клиентском заказе заменяет текущее изделие, а не создаёт
 * второе независимое изделие в той же карточке производства. Комната,
 * название и выбранное оформление проекта при этом сохраняются.
 */
export function replaceWithCatalogShelf(project: PlannerProject, source: WardrobeModule): PlannerProject {
  const emptyProject: PlannerProject = {
    ...structuredClone(project),
    modules: [],
    selectedModuleId: null,
    selectedItemId: null,
    selectedShelvingCellId: null
  };
  return appendCatalogShelf(emptyProject, source);
}

/**
 * Готовый шкаф — это состав: корпуса, антресоли, угловой, торец. Он заменяет
 * текущее изделие целиком. Каждому корпусу выдаётся свой идентификатор, а связи
 * между ними (антресоль на корпусе, торец у корпуса, общий пролёт) переносятся
 * на новые идентификаторы — иначе антресоль «повиснет» без опоры.
 */
export function replaceWithCatalogComposition(project: PlannerProject, sources: WardrobeModule[]): PlannerProject {
  if (!sources.length || sources.some(source => source.type === "shelving")) {
    throw new Error("Эта модель не является шкафом");
  }
  const ids = new Map(sources.map(source => [source.id, createId("module")]));
  const runs = new Map<string, string>();
  const modules = sources.map(source => {
    const module = structuredClone(source);
    module.id = ids.get(source.id)!;
    if (module.stackedOnId) module.stackedOnId = ids.get(module.stackedOnId);
    if (module.endAttachedToId) module.endAttachedToId = ids.get(module.endAttachedToId);
    if (module.cabinetRunId) {
      if (!runs.has(module.cabinetRunId)) runs.set(module.cabinetRunId, createId("run"));
      module.cabinetRunId = runs.get(module.cabinetRunId);
    }
    module.items = module.items.map(item => ({ ...item, id: createId("item") }));
    return module;
  });
  const draft: PlannerProject = {
    ...structuredClone(project),
    modules,
    selectedModuleId: null,
    selectedItemId: null,
    selectedShelvingCellId: null
  };
  if (maxHeight(draft) > usableRoomHeightMm(draft)) {
    throw new Error("Модель не помещается у потолка: она входит в потолочную падугу. Сначала увеличьте высоту комнаты");
  }
  // Выбирается основной корпус, а не антресоль и не торец: с него начинается настройка.
  draft.selectedModuleId = (
    modules.find(module => !module.stackedOnId && module.type !== "end") ?? modules[0]!
  ).id;
  return draft;
}
